import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
  useMediaQuery,
  Box,
} from '@mui/material';
import { Close, Dvr } from '@mui/icons-material';
import { useAccount } from '../../../contexts/Account';
import ACSnackbar from '../../ACSnackbar';
import eventBus from '../../../eventBus';

const useStyles = () => ({
  container: {
    borderRadius: '10px',
    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.07)',
    maxWidth: '600px',
    padding: {
      xxs: '48px 0px 0px',
      xs: '30px',
    },
  },
  textHeadline: {
    fontWeight: '600',
    fontSize: '20px',
    padding: '8px 10px',
    color: '#4D586F',
  },
  textContent: {
    fontWeight: '400',
    fontSize: '14px',
    padding: '8px 10px',
    color: '#001030',
    padding: '0px',
    mb: '24px',
  },
  dialogActions: {
    width: '100%',
    marginTop: '8px',
    padding: '8px 12px',
  },
  mainButton: {
    width: '100%',
    textTransform: 'none',
    fontWeight: '600',
    fontSize: '14px',
    height: '45px',
    borderRadius: '12px',
  },
});

export default function CopyModal() {
  const { accountDetails } = useAccount();
  const joinUrl = `https://app.advisorycloud.com/join/advisors/${accountDetails.slug}/${accountDetails.id}`;
  const customData = {
    facebook: {
      title: 'Facebook',
      icon: '/icons/facebook_logo.svg',
      text: 'Click the button below to copy the announcement and paste it into a new post on Facebook.',
      input: `I've built an Advisory Board on AdvisoryCloud, and would love for you to join!\n${joinUrl}`,
    },
    x: {
      title: 'X',
      icon: '/icons/x_logo.svg',
      text: 'Click the button below to copy the announcement and paste it into a new post on X.',
      input: `I've built an Advisory Board on AdvisoryCloud, and would love for you to join!\n${joinUrl}`,
    },
    code: {
      title: 'your website',
      icon: '/icons/x_logo.svg',
      text: "We've made it easy to share your advisory board through your website, so your priority visitors and customers can join!",
      input: `
      <a href="${joinUrl}" target="_blank" rel="noopener noreferrer">
        <img
          src="https://app.advisorycloud.com/images/JoinMyBoardButton.png"
          alt="Join My Board Button"
          style={{
            display: 'block',
            width: '170px',
            height: '46px',
            cursor: 'pointer',
            marginRight: '10px',
          }}
        />
      </a>
      `,
    },
  };
  const classes = useStyles();
  const [modalType, setModalType] = useState('code');
  const [inputValue, setInputValue] = useState(customData[modalType]?.input);
  const [open, setOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  function handleClose() {
    setOpen(false);
  }

  useEffect(() => {
    eventBus.on('openCopyModal', (data) => {
      setOpen(true);
      setModalType(data?.modalType || 'code');
    });
    return () => {
      eventBus.remove('openCopyModal');
    };
  }, []);

  useEffect(() => {
    setInputValue(customData[modalType]?.input);
  }, [modalType]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isXS}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: classes.container,
      }}
    >
      <Close
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: '12px',
          top: '12px',
          color: '#4D586F',
        }}
      />
      <DialogTitle
        id="alert-dialog-title"
        display="flex"
        sx={{ ...classes.textHeadline, padding: '12px 12px' }}
      >
        {modalType === 'code' ? (
          <Dvr
            sx={{
              color: '#0866FF',
              m: '0px 10px',
              fontSize: '1.7rem',
            }}
          />
        ) : (
          <img
            src={customData[modalType]?.icon}
            alt={`${customData[modalType]?.title} Logo`}
            style={{ marginRight: '12px' }}
          />
        )}
        Share on {customData[modalType]?.title}
      </DialogTitle>
      <DialogContent sx={{ padding: '8px 12px' }}>
        <Typography sx={classes.textContent}>
          {customData[modalType]?.text}
        </Typography>
        {modalType === 'code' ? (
          <Box display="flex">
            <img
              src="images/JoinMyBoardButton.png"
              alt="Join My Board Button"
              style={{
                display: 'block',
                width: '170px',
                height: '46px',
                cursor: 'pointer',
                marginRight: '10px',
              }}
            />
            <Typography sx={classes.textContent}>
              Click below to copy the code to add this button to your website.
            </Typography>
          </Box>
        ) : (
          <TextField
            id="advisorHeadline"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={inputValue}
            onChange={(evt) => setInputValue(evt.target.value)}
            inputProps={{
              maxLength: 200,
            }}
            InputProps={{
              style: {
                borderRadius: '8px',
              },
            }}
          />
        )}
      </DialogContent>
      <DialogActions sx={classes.dialogActions}>
        <Button
          color="primary"
          variant='contained'
          onClick={() => {
            navigator.clipboard.writeText(inputValue);
            setSuccessMessage(
              `${
                modalType === 'code' ? 'Code snippet' : 'Text'
              } copied, you can now share it with whoever you choose.`
            );
            setSuccessSnackbar(true);
          }}
          sx={{
            ...classes.mainButton,
            color: '#ffffff',
            backgroundColor: '#2B71F6',
            '&:hover': {
              backgroundColor: '#2B71F6',
            },
          }}
        >
          Copy{modalType === 'code' ? ' Code <>' : ''}
        </Button>
      </DialogActions>
      <ACSnackbar
        open={successSnackbar}
        text={successMessage}
        severity="success"
        onClose={() => {
          setSuccessSnackbar(false);
        }}
        autoHideDuration={6000}
      />
    </Dialog>
  );
}
